/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useRecurrence.js
 *
 * Purpose: Manages per-sample/assay/panel test recurrence (repeat testing dates) state and
 *     derives create payloads incorporating those dates.
 *
 * Scope: recurring test scheduling within the order request test-selection flow.
 *
 * Tags: customer-portal, orders
 *
 * Overview: Keys each recurrence entry by a JSON-stringified [sampleId, assayId, panelId]
 *     tuple, storing entries in state gated by useRecurrenceParameters' isEnabled flag.
 *     Provides getRecurrence/setRecurrence/removeRecurrence to read, merge, and bulk-clear (via
 *     regex key matching) recurrence entries, checkEditing to detect whether a sample/panel id
 *     pair maps to an existing recurrence, and createPayload to expand a base test payload into
 *     one entry per recurrence date, dropping the original estimated start/complete date fields
 *     when dates are present.
 *
 * Exports: default useRecurrence hook; named defaultValue constant (the empty/default
 *     recurrence shape); named selectRecurrenceEntryPattern, exported for direct test coverage
 *     (not part of this module's normal public API) since its four branches are otherwise
 *     indistinguishable through removeRecurrence's own public surface -- three build an
 *     equally-unsatisfiable regex, so asserting only removeRecurrence's filtering *effect*
 *     cannot tell which branch actually ran.
 */

import { useState } from 'react';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import useRecurrenceParameters from './useRecurrenceParameters';

export const defaultValue = {
  dates: [],
  shouldAutoFill: true,
  businessDaysOnly: true,
};

function getKey(sampleId, assayId, panelId) {
  if (!sampleId || !assayId) return;
  return JSON.stringify(
    panelId ? [sampleId, assayId, panelId] : [sampleId, assayId],
  );
}

// Selects the regex source string removeRecurrence uses to bulk-clear matching entries, in the
// exact priority order the original if/else-if/else-if/else-if chain used: assayId+panelId
// first, then sampleId, then assayId, then panelId, then undefined (no scoping id given).
// Sequential guard-clause returns preserve that priority identically for overlapping truthy
// conditions (e.g. sampleId+panelId both present still selects the sampleId branch, not
// panelId's), without the chained if/else-if form's nesting-depth cost.
//
// PRESERVED PRE-EXISTING BEHAVIOR (not fixed here): removeRecurrence's four branches are ALL
// broken, in two different ways -- none of them removes only the scoped entry the way the
// function's name implies.
//   - The sampleId-only, assayId-only, and panelId-only branches wrap their pattern in literal
//     `/.../ ` characters inside the template string (e.g. `` `/^\[${sampleId},...\]$/` ``).
//     new RegExp() has no delimiter syntax (unlike a `/pattern/` literal) -- every character in
//     the string is part of the pattern, so the leading `/` is a literal character to match,
//     immediately followed by `^`. `^` asserts "this position is the very start of the string"
//     wherever it appears, not just when it opens the pattern; requiring a character (the
//     literal `/`) to match and THEN asserting that same later position is still position 0 is a
//     contradiction no input can satisfy -- consuming one character always advances past
//     position 0. So regex.test(key) is false for every possible key, not merely for realistic
//     ones (confirmed: even a key that itself starts with "/" does not match). Combined with
//     removeRecurrence's
//     `omitBy(recurrence, (_, key) => !regex.test(key))`, a never-matching regex makes every
//     entry's key satisfy the omit condition, so these three branches wipe the ENTIRE recurrence
//     state rather than scoping the removal.
//   - The assayId+panelId branch's regex is the only one that matches correctly, but that makes
//     its effect through the same omitBy call INVERTED, not correct: omitBy omits entries the
//     predicate is truthy for (entries the regex does NOT match), so the one entry that DOES
//     match survives and every OTHER entry is removed -- backwards from "remove this scope,
//     keep the rest". See useRecurrence.test.js for both bugs pinned by direct assertion.
// This is real, confirmed, pre-existing behavior, preserved exactly here rather than silently
// fixed or silently reproduced without comment.
export function selectRecurrenceEntryPattern({ sampleId, assayId, panelId }) {
  if (assayId && panelId) {
    // Scoped to a specific assay within a specific panel.
    return `^\\[[1-9]+,${assayId},${panelId}\\]$`;
  }
  if (sampleId) {
    return `/^\[${sampleId},[1-9]+(,[1-9]+)?\]$/`;
  }
  if (assayId) {
    return `/^\[[1-9]+,${assayId},[1-9]+\]$/`;
  }
  if (panelId) {
    return `/^\[[1-9]+,[1-9]+,${panelId}\]$/`;
  }
  return undefined;
}

export default function useRecurrence() {
  const { isEnabled } = useRecurrenceParameters();
  const [recurrence, addRecurrence] = useState({});

  function getRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return defaultValue;
    return recurrence[getKey(sampleId, assayId, panelId)] || defaultValue;
  }

  function setRecurrence({ sampleId, assayId, panelId, ...value }) {
    if (!isEnabled) return;
    const key = getKey(sampleId, assayId, panelId);
    const current = getRecurrence({ sampleId, assayId, panelId });
    addRecurrence((prev) => ({
      ...prev,
      [key]: {
        ...current,
        ...value,
      },
    }));
  }

  function removeRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return;

    const entRegex = selectRecurrenceEntryPattern({
      sampleId,
      assayId,
      panelId,
    });
    const regex = new RegExp(entRegex);
    const newVal = omitBy(recurrence, (_, key) => !regex.test(key));
    addRecurrence(newVal);
  }

  function checkEditing({ id, sampleId, isPanel }) {
    return (
      Object.keys(recurrence)
        .map(JSON.parse)
        // Panel-based recurrences...
        .filter((ids) => (ids.length === isPanel ? 3 : 2))
        // with ID matching `panelId`.
        .some(
          (ids) =>
            ids.at(0) === parseInt(sampleId) && ids.at(-1) === parseInt(id),
        )
    );
  }

  function createPayload(testPayload) {
    if (!isEnabled) return testPayload;
    return testPayload
      .map((payload) => {
        const {
          estimated_start_date: _,
          estimated_complete_date: __,
          ...test
        } = payload;
        const {
          sample_id: sampleId,
          assay_id: assayId,
          panel_id: panelId,
        } = test;
        const { dates } = getRecurrence({
          assayId,
          sampleId,
          ...(panelId && { panelId }),
        });

        return isEmpty(dates)
          ? payload
          : dates.map(({ start, end }) => ({
              ...test,
              ...(start && { estimated_start_date: start }),
              ...(end && { estimated_complete_date: end }),
            }));
      })
      .flat(2);
  }

  return {
    createPayload,
    checkEditing,
    getRecurrence,
    setRecurrence,
    removeRecurrence,
    recurrence: isEnabled ? recurrence : {},
  };
}
